import React, { createContext, useState, useMemo, useCallback, useRef } from 'react'

import { ViewProps } from 'tamagui'

import { Text } from '../Text'
import { Button } from '../Button/Button'
import { Link } from '../Link'
import { Icon } from '../Icon'
import { LogoIconAnimated } from '../Logo/LogoIconAnimated'

import { DynamicIslandHomeExpand } from './DynamicIslandHomeExpand'

import { DynamicIslandAddExpand } from './DynamicIslandAddExpand'

import { DynamicIslandProfileButton } from './DynamicIslandProfileButton'
import { DynamicIslandProfileExpand } from './DynamicIslandProfileExpand'

export type Item = {
  id: string
  name?: string
  component:
    | React.ReactNode
    | (({ isLoading }: { isLoading: boolean }) => React.ReactNode)
  child?: React.ReactNode
  containerStyle?: ViewProps
  meta?: Record<string, unknown>
}

export const DynamicIslandRowsContext = createContext<{
  rows: Array<Array<Item>>
  setRows: React.Dispatch<React.SetStateAction<Array<Array<Item>>>>
  pushRows: (rows: Array<Array<Item>>) => void
  popRows: () => void
}>({
  rows: [],
  setRows: () => {},
  pushRows: () => {},
  popRows: () => {},
})

export const DynamicIslandProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [dynamicIslandRows, setDynamicIslandRows] = useState<
    Array<Array<Item>>
  >(() => [
    [
      {
        id: 'logo',
        component: ({ isLoading }) => {
          return (
            <Link
              key='home'
              href='/'
              hoverStyle={{ textDecorationLine: 'none' }}
              width='$8'
              height='$8'
            >
              <LogoIconAnimated width='$8' height='$8' animate={isLoading} />
            </Link>
          )
        },
        containerStyle: {
          // marginRight: '$5',
          $gtXs: {
            marginRight: '$5',
          },
        },
      },
      {
        id: 'logo-word',
        component: (
          <Link key='home' href='/' hoverStyle={{ textDecorationLine: 'none' }}>
            <Text fontSize='$6' fontWeight='$7' color='$color'>
              Guild
            </Text>
          </Link>
        ),
        containerStyle: {
          display: 'flex',
          marginRight: '$3',

          $gtXs: {
            display: 'none',
          },
        },
      },
      {
        id: 'home-menu',
        name: 'Home',
        component: (
          <Button
            key='profile'
            minHeight='$8'
            height='$8'
            width='$8'
            padding='$0'
            alignItems='center'
            justifyContent='center'
            secondary
          >
            <Icon name='navigation' />
          </Button>
        ),
        child: <DynamicIslandHomeExpand />,
        containerStyle: {
          display: 'flex',
          $gtXs: {
            display: 'none',
          },
        },
      },
      {
        id: 'guilds',
        component: (
          <Link
            key='guilds'
            href='/guilds'
            hoverStyle={{ textDecorationLine: 'none' }}
          >
            <Text color='$color'>Guilds</Text>
          </Link>
        ),
        containerStyle: {
          marginRight: '$1',
          display: 'none',
          $gtXs: {
            display: 'flex',
          },
        },
      },
      {
        id: 'events',
        component: (
          <Link
            key='events'
            href='/events'
            hoverStyle={{ textDecorationLine: 'none' }}
          >
            <Text color='$color'>Events</Text>
          </Link>
        ),
        containerStyle: {
          marginHorizontal: '$1',
          display: 'none',
          $gtXs: {
            display: 'flex',
          },
        },
      },
      {
        id: 'presentations',
        component: (
          <Link
            key='presentations'
            href='/presentations'
            hoverStyle={{ textDecorationLine: 'none' }}
          >
            <Text color='$color'>Presentations</Text>
          </Link>
        ),
        containerStyle: {
          marginLeft: '$1',
          marginRight: '$5',
          display: 'none',
          $gtXs: {
            display: 'flex',
          },
        },
      },
      {
        id: 'create',
        name: 'Create',
        component: (
          <Button
            key='create'
            minHeight='$8'
            height='$8'
            width='$8'
            padding='$0'
            alignItems='center'
            justifyContent='center'
            secondary
          >
            <Icon name='add' />
          </Button>
        ),
        child: <DynamicIslandAddExpand />,
      },
      {
        id: 'profile',
        name: 'Profile',
        component: <DynamicIslandProfileButton />,
        child: <DynamicIslandProfileExpand />,
      },
    ],
  ])

  // Row stack for push/pop
  const savedRowsRef = useRef<Array<Array<Item>> | null>(null)

  const pushRows = useCallback((rows: Array<Array<Item>>) => {
    setDynamicIslandRows((current) => {
      if (!savedRowsRef.current) {
        savedRowsRef.current = current
      }
      return rows
    })
  }, [])

  const popRows = useCallback(() => {
    if (savedRowsRef.current) {
      setDynamicIslandRows(savedRowsRef.current)
      savedRowsRef.current = null
    }
  }, [])

  const contextValue = useMemo(() => {
    return {
      rows: dynamicIslandRows,
      setRows: setDynamicIslandRows,
      pushRows,
      popRows,
    }
  }, [dynamicIslandRows, setDynamicIslandRows, pushRows, popRows])

  return (
    <DynamicIslandRowsContext.Provider value={contextValue}>
      {children}
    </DynamicIslandRowsContext.Provider>
  )
}
